import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Btn, Loading, Msg, Section, Select, fmtDate, panelGet, useAdminGet } from '../kit';
import { type FlowGraphData, type FlowNode, type FlowScenario, type FlowTrace, type TraceStep, asList, num, statusStyle } from '../search/flowTypes';
import {
  type Layout, type Timeline, computeLayout, frameAt, laneOrder, normalizePath, resolvePathEdges, scenarioTimeline,
  traceTimeline,
} from '../search/flowLayout';
import FlowGraph, { FLOW_CSS, FlowLegend, type FlowMode, type Selection } from '../search/FlowGraph';
import FlowNodePanel, { type NavItem } from '../search/FlowNodePanel';
import {
  MODE_LABEL, type RecrawlCtx, S, StatusPill, arr, fmtAgo, fmtDur, fmtIST, fmtMsDur, fmtN, fmtUsd, obj, scheduleText, str,
} from './shared';

/**
 * Recrawl → Flow: one university's pipeline as the Search-flow graph
 * (GET flow {code} → recrawl/flow.py, the same {nodes, edges, scenarios}
 * shape admin_search_flow.py returns), drawn by the search components
 * themselves — FlowGraph, FlowNodePanel, flowLayout, flowTypes — imported
 * unchanged, so the two flows look and behave alike.
 *
 *  - Overview: every step with its live parameters (declared vs effective
 *    throttle, guard thresholds, approval rules, budgets) and where each value
 *    comes from; click a step or an edge for the details.
 *  - Scenarios: the documented paths (common, first run, partial scrape
 *    held, removal approval, site blocked, dry run, manual upload) animated,
 *    each with the latest real run that took it.
 *  - Replay: a stored run (GET flow_replay {run_id}) travelling the graph,
 *    each step dwelling in proportion to its real duration, plus the step
 *    Gantt. There is no live execution from here: a run is started from the
 *    university drawer, then replayed.
 *
 * Two adaptations, both on this side: the nodes are ordered by the lanes the
 * server sends (flowLayout orders unknown lanes by first appearance, and a
 * risk node can appear before its lane's main step), and the lane titles use
 * the server's lane_labels. The player and the Gantt are local copies of the
 * Search-flow ones (they are not exported there), with durations in h / m / s
 * since a crawl runs for minutes to hours, not milliseconds.
 */

type Graph = FlowGraphData & { laneLabels: Record<string, string> };

function useWidth(): [(el: HTMLDivElement | null) => void, number] {
  const [w, setW] = useState(0);
  const ro = useRef<ResizeObserver | null>(null);
  const ref = useCallback((el: HTMLDivElement | null) => {
    ro.current?.disconnect();
    ro.current = null;
    if (!el) return;
    setW(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    ro.current = new ResizeObserver(entries => { for (const e of entries) setW(Math.round(e.contentRect.width)); });
    ro.current.observe(el);
  }, []);
  useEffect(() => () => ro.current?.disconnect(), []);
  return [ref, w];
}

function sanitizeGraph(d: any): Graph | null {
  if (!d || typeof d !== 'object') return null;
  const seen = new Set<string>();
  let nodes: FlowNode[] = (Array.isArray(d.nodes) ? d.nodes : [])
    .filter((n: any) => n && n.id && !seen.has(String(n.id)) && seen.add(String(n.id)))
    .map((n: any) => ({
      ...n, id: String(n.id), label: String(n.label ?? n.id), lane: String(n.lane || 'scrape'),
      order: num(n.order) ?? 0, kind: String(n.kind || 'step'),
      params: Array.isArray(n.params) ? n.params : [],
      substeps: Array.isArray(n.substeps) ? n.substeps.filter((s: any) => s && s.id).map((s: any) => ({
        ...s, id: String(s.id), label: String(s.label ?? s.id), params: Array.isArray(s.params) ? s.params : [],
      })) : [],
    }));
  // flowLayout orders lanes it does not know by first appearance: sort by the server's lane order first.
  const lanes: string[] = Array.isArray(d.lanes) ? d.lanes.map(String) : [];
  if (lanes.length) {
    const idx = (l: string) => { const i = lanes.indexOf(l); return i < 0 ? lanes.length : i; };
    nodes = nodes.map((n, i) => ({ n, i })).sort((a, b) => idx(a.n.lane) - idx(b.n.lane) || a.i - b.i).map(x => x.n);
  }
  const ids = new Set(nodes.map(n => n.id));
  const subIds = new Set(nodes.flatMap(n => (n.substeps || []).map(s => s.id)));
  const eSeen = new Set<string>();
  const edges = (Array.isArray(d.edges) ? d.edges : [])
    .filter((e: any) => e && (ids.has(String(e.from)) || subIds.has(String(e.from)))
      && (ids.has(String(e.to)) || subIds.has(String(e.to))))
    .map((e: any, i: number) => ({ ...e, id: String(e.id ?? `e${i}`), from: String(e.from), to: String(e.to) }))
    .filter((e: any) => !eSeen.has(e.id) && eSeen.add(e.id));
  const scenarios: FlowScenario[] = (Array.isArray(d.scenarios) ? d.scenarios : [])
    .filter((s: any) => s && s.id)
    .map((s: any) => ({ ...s, id: String(s.id), label: String(s.label ?? s.id),
                        path: Array.isArray(s.path) ? s.path.map(String) : [],
                        edges: Array.isArray(s.edges) ? s.edges.map(String) : [],
                        real_run: s.real_run && typeof s.real_run === 'object' && !Array.isArray(s.real_run) ? s.real_run : null }));
  return { nodes, edges, scenarios, live: d.live || null, generated_at: d.generated_at, laneLabels: obj(d.lane_labels) };
}

/** The server's lane titles over flowLayout's (which only knows the search lanes). */
const relabel = (l: Layout, labels: Record<string, string>): Layout =>
  ({ ...l, lanes: l.lanes.map(b => ({ ...b, label: str(labels[b.lane]) || b.label.replace(/_/g, ' ') })) });

// ---------------------------------------------------------------------------
// player (a copy of the Search-flow clock)
// ---------------------------------------------------------------------------
function usePlayer(timeline: Timeline | null) {
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const tRef = useRef(0);
  const speedRef = useRef(1);
  speedRef.current = speed;
  const totalRef = useRef(0);
  totalRef.current = timeline?.total || 0;
  const reduced = useMemo(() => {
    try { return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
  }, []);
  const restart = useCallback(() => { tRef.current = 0; setT(0); setPlaying(true); }, []);
  useEffect(() => {
    if (!timeline) { tRef.current = 0; setT(0); setPlaying(false); return; }
    if (reduced) { tRef.current = timeline.total; setT(timeline.total); setPlaying(false); return; }
    restart();
  }, [timeline, reduced, restart]);
  useEffect(() => {
    if (!playing) return undefined;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(100, Math.max(0, now - last));
      last = now;
      tRef.current = Math.min(totalRef.current, tRef.current + dt * speedRef.current);
      setT(tRef.current);
      if (tRef.current >= totalRef.current) { setPlaying(false); return; }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);
  return {
    t, playing, speed, setSpeed, restart, total: timeline?.total || 0,
    toggle: () => { if (tRef.current >= totalRef.current) restart(); else setPlaying(p => !p); },
    skip: () => { tRef.current = totalRef.current; setT(totalRef.current); setPlaying(false); },
    seek: (v: number) => { tRef.current = Math.max(0, Math.min(totalRef.current, v)); setT(tRef.current); },
  };
}

const PlayerBar: React.FC<{
  player: ReturnType<typeof usePlayer>; finished: boolean; activeLabel: string; trace: boolean; realNow: number; realTotal: number;
}> = ({ player, finished, activeLabel, trace, realNow, realTotal }) => {
  const pct = player.total ? (player.t / player.total) * 100 : 0;
  return (
    <div className="flex items-center gap-2 flex-wrap flex-1 min-w-[18rem]">
      <button type="button" onClick={player.restart} title="Replay from the start"
        className="h-7 px-2.5 rounded-full border border-grayscale-600 text-xs hover:bg-grayscale-200">⟲ Replay</button>
      <button type="button" onClick={player.toggle} title={player.playing ? 'Pause' : 'Play'}
        className="h-7 w-9 rounded-full bg-secondary-500 text-white text-xs">{player.playing ? '❚❚' : '▶'}</button>
      <button type="button" onClick={player.skip} disabled={finished} title="Skip to end"
        className="h-7 px-2.5 rounded-full border border-grayscale-600 text-xs hover:bg-grayscale-200 disabled:opacity-40">⏭ Skip to end</button>
      <label className="flex items-center gap-1 text-[11px] text-grayscale-1100" title="Replay speed">
        speed
        <input type="range" min={0.25} max={3} step={0.25} value={player.speed}
          onChange={e => player.setSpeed(Number(e.target.value))} className="w-20 accent-[#2C5695]" />
        <span className="w-8 text-grayscale-1300 font-medium">{player.speed}×</span>
      </label>
      <div className="flex-1 min-w-[8rem] flex flex-col gap-0.5">
        <div className="h-1.5 rounded-full bg-grayscale-200 overflow-hidden cursor-pointer"
          onClick={e => {
            const r = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
            player.seek(((e.clientX - r.left) / r.width) * player.total);
          }}>
          <div className="h-full bg-secondary-500" style={{ width: `${pct}%` }} />
        </div>
        <div className="flex justify-between text-[10.5px] text-grayscale-1100">
          <span className="truncate">{activeLabel}</span>
          {trace && <span>t = {fmtMsDur(realNow)} of {fmtMsDur(realTotal)} real</span>}
        </div>
      </div>
    </div>
  );
};

const Seg: React.FC<{ value: string; onChange: (v: string) => void; options: [string, string][]; title?: string }> =
  ({ value, onChange, options, title }) => (
  <div className="inline-flex rounded-full border border-grayscale-600 p-0.5 text-[11px]" title={title}>
    {options.map(([v, l]) => (
      <button key={v} type="button" onClick={() => onChange(v)}
        className={`px-2 py-0.5 rounded-full whitespace-nowrap ${value === v ? 'bg-primary-500 text-white font-bold' : 'text-grayscale-1200 hover:bg-grayscale-200'}`}>
        {l}
      </button>
    ))}
  </div>
);

/** The replayed run's steps as a Gantt on its real clock (a copy of the Search-flow StepTimeline). */
const StepTimeline: React.FC<{
  steps: TraceStep[]; path: string[]; graph: FlowGraphData; realTotal: number; realNow: number | null;
  onSelect: (s: Selection) => void;
}> = ({ steps, path, graph, realTotal, realNow, onSelect }) => {
  const [open, setOpen] = useState(true);
  const byId = useMemo(() => new Map(graph.nodes.map(n => [n.id, n])), [graph]);
  const rows = useMemo(() => [...steps].map((s, i) => ({ s, i }))
    .sort((a, b) => (num(a.s.t_start_ms) ?? 0) - (num(b.s.t_start_ms) ?? 0) || a.i - b.i).map(x => x.s), [steps]);
  const span = Math.max(1, realTotal || 0, ...steps.map(s => (num(s.t_start_ms) ?? 0) + (num(s.ms) ?? 0)));
  const pct = (v: number) => `${Math.max(0, Math.min(100, (v / span) * 100))}%`;
  return (
    <Section title={`Step timeline — ${steps.length} steps over ${fmtMsDur(span)} · ${path.length} nodes on the path`}
      right={<Btn small onClick={() => setOpen(o => !o)}>{open ? 'Hide' : 'Show'}</Btn>}>
      {open && (
        <div className="flex flex-col gap-0.5 overflow-x-auto">
          <div className="flex items-center gap-2 text-[10px] text-grayscale-1100 min-w-[36rem]">
            <div className="w-56 shrink-0" />
            <div className="flex-1 relative h-3">
              {[0, 0.25, 0.5, 0.75, 1].map(f => (
                <span key={f} className={`absolute whitespace-nowrap ${f === 0 ? '' : f === 1 ? '-translate-x-full' : '-translate-x-1/2'}`}
                  style={{ left: `${f * 100}%` }}>{fmtMsDur(span * f)}</span>
              ))}
            </div>
            <div className="w-24 shrink-0" />
          </div>
          {rows.map((s, i) => {
            const n = byId.get(s.node_id);
            const t0 = num(s.t_start_ms); const ms = num(s.ms) ?? 0;
            const st = statusStyle(s.status);
            const decision = asList(s.notes).some(x => x.startsWith('decision'));
            return (
              <button key={i} type="button" onClick={() => onSelect({ kind: 'node', id: s.node_id })}
                className="flex items-center gap-2 text-left rounded hover:bg-grayscale-200 px-1 min-w-[36rem]">
                <div className={`w-56 shrink-0 truncate text-[12px] ${decision ? 'pl-4 text-grayscale-1100' : 'font-medium text-grayscale-1300'}`}
                  title={asList(s.notes).join(' · ')}>
                  {decision ? `◇ ${n?.label || s.node_id}` : (n?.label || s.node_id)}
                </div>
                <div className="flex-1 relative h-3.5 bg-grayscale-200 rounded">
                  {t0 !== null && (
                    <div className="absolute top-0 bottom-0 rounded"
                      style={{ left: pct(t0), width: `max(3px, ${pct(ms)})`, background: st.stroke, opacity: s.status === 'skipped' ? 0.45 : 0.9 }} />
                  )}
                  {realNow !== null && <div className="absolute top-[-2px] bottom-[-2px] w-px bg-secondary-500" style={{ left: pct(realNow) }} />}
                </div>
                <div className="w-24 shrink-0 flex items-center justify-end gap-1.5">
                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: st.stroke }} title={String(s.status)} />
                  <span className="text-[11.5px] text-grayscale-1200">{s.status === 'skipped' && !ms ? 'skipped' : fmtMsDur(ms)}</span>
                </div>
              </button>
            );
          })}
          {!rows.length && <div className="text-sm text-grayscale-1100 py-2">The run recorded no steps.</div>}
        </div>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// pieces
// ---------------------------------------------------------------------------
const LiveStrip: React.FC<{ live: any; generatedAt?: string; loading: boolean; onRefresh: () => void }> = ({ live, generatedAt, loading, onRefresh }) => {
  const l = obj(live);
  const m = obj(l.master);
  const th = obj(obj(l.throttle).effective);
  const st = obj(l.settings);
  const due = obj(l.due);
  const pill = (label: string, value: React.ReactNode, title?: string, tone: 'plain' | 'ok' | 'warn' = 'plain') => (
    <div title={title} className={`rounded-xl border px-3 py-1.5 min-w-0 ${tone === 'warn' ? 'border-amber-300 bg-amber-50' : tone === 'ok' ? 'border-green-200 bg-green-50' : 'border-grayscale-600/30 bg-white'}`}>
      <div className="text-[10px] uppercase tracking-wide text-grayscale-1100">{label}</div>
      <div className="text-[13px] font-bold text-grayscale-1300 font-grotesk truncate max-w-[16rem]">{value}</div>
    </div>
  );
  return (
    <div className="flex items-stretch gap-2 flex-wrap">
      {pill('Kind', `${l.kind === 'manual_file' ? 'manual file' : str(l.kind) || '—'}${l.platform ? ` · ${str(l.platform)}` : ''}`)}
      {pill('Master', `${fmtN((m.active ?? 0) + (m.missing ?? 0))} listed`, `${fmtN(m.active)} active · ${fmtN(m.missing)} missing · ${fmtN(m.removed)} removed`)}
      {pill('Live rows', fmtN(l.live_count))}
      {pill('Engine runs', `${fmtN(l.engine_runs)}${l.first_engine_run ? ' · first run pending' : ''}`,
        l.first_engine_run ? 'The first engine run of a university always waits for approval' : undefined, l.first_engine_run ? 'warn' : 'plain')}
      {pill('Schedule', scheduleText(l.schedule), str(due.reason))}
      {pill('Next due', due.next_at ? fmtAgo(due.next_at) : '—', due.next_at ? `${fmtIST(due.next_at)} — ${str(due.reason)}` : str(due.reason))}
      {pill('Throttle', `${th.max_workers ?? '?'} workers · ${th.min_interval_s ?? '?'} s`, 'Effective governor throttle (manifest default + overrides)')}
      {pill('Auto-approve', st.auto_approve ? 'on' : 'off', 'Settings → auto_approve', st.auto_approve ? 'warn' : 'plain')}
      {pill('Scheduler', st.enabled ? 'on' : 'off', 'Settings → enabled (needs RECRAWL_AUTO=1 too)', st.enabled ? 'ok' : 'plain')}
      <div className="flex flex-col justify-center gap-1 ml-auto">
        <Btn small onClick={onRefresh} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</Btn>
        {generatedAt && <div className="text-[10.5px] text-grayscale-1100 text-right">graph built {fmtDate(generatedAt)}</div>}
      </div>
    </div>
  );
};

const runSummary = (r: any) => {
  const c = obj(obj(r).counts);
  const bits: string[] = [];
  ([['mapped', 'mapped'], ['new', 'new'], ['modified', 'modified'], ['moved', 'moved'], ['removed_proposed', 'removals proposed']] as const).forEach(([k, label]) => {
    if (num(c[k]) !== null) bits.push(`${label} ${fmtN(c[k])}`);
  });
  return bits.join(' · ');
};

const ScenarioCard: React.FC<{ s: FlowScenario; graph: FlowGraphData; onReplay: () => void; onReplayRun: (id: string) => void; busy: string }> =
  ({ s, graph, onReplay, onReplayRun, busy }) => {
  const labels = new Map(graph.nodes.map(n => [n.id, n.label]));
  const notes = asList(s.notes);
  const ex = obj(s.example);
  const rr = s.real_run ? obj(s.real_run) : null;
  return (
    <div className="rounded-xl border border-secondary-400 bg-secondary-100/40 p-3 flex flex-col gap-2 min-w-0 [overflow-wrap:anywhere]">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-grayscale-1300 font-grotesk">{s.label}</div>
          {s.description && <p className="text-[13px] text-grayscale-1200 mt-0.5 whitespace-pre-line">{s.description}</p>}
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <Btn small onClick={onReplay}>⟲ Replay</Btn>
          {rr && num(rr.run_id) !== null && (
            <Btn small kind="primary" disabled={!!busy} onClick={() => onReplayRun(str(rr.run_id))}
              title="Replay the latest real run that took this path">{busy === str(rr.run_id) ? 'Loading…' : `▶ Replay run #${str(rr.run_id)}`}</Btn>
          )}
        </div>
      </div>
      {Object.keys(ex).length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-grayscale-1200">
          {Object.entries(ex).map(([k, v]) => <span key={k}><span className="text-grayscale-1100">{k} </span><b>{str(v)}</b></span>)}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1 text-[11px]">
        {s.path.map((id, i) => (
          <React.Fragment key={`${id}-${i}`}>
            {i > 0 && <span className="text-grayscale-1100">→</span>}
            <span className="rounded-full bg-white border border-grayscale-600/30 px-2 py-px text-grayscale-1200">{labels.get(id) || id}</span>
          </React.Fragment>
        ))}
      </div>
      {notes.length > 0 && <ul className="list-disc pl-5 text-[12px] text-grayscale-1200">{notes.map((n, i) => <li key={i}>{n}</li>)}</ul>}
      {rr ? (
        <div className="rounded-lg border border-grayscale-600/30 bg-white px-2.5 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-grayscale-1200">
          <span className="text-[11px] font-bold text-grayscale-1100">Latest real run</span>
          <span>#{str(rr.run_id)}</span>
          <StatusPill status={rr.status} />
          <span>{MODE_LABEL[str(rr.mode)] || str(rr.mode)}</span>
          <span title={fmtIST(rr.date)}>{fmtAgo(rr.date)}</span>
          {runSummary(rr) && <span>{runSummary(rr)}</span>}
          {num(obj(rr.cost).est_usd) !== null && <span>est. {fmtUsd(obj(rr.cost).est_usd)}</span>}
        </div>
      ) : <div className="text-[11px] text-grayscale-1100">No real run has taken this path yet.</div>}
    </div>
  );
};

const ReplayHeader: React.FC<{ data: any; graph: FlowGraphData; ctx: RecrawlCtx; onScenario: (id: string) => void; onClear: () => void }> =
  ({ data, graph, ctx, onScenario, onClear }) => {
  const r = obj(data.run);
  const guess = data.scenario_guess ? graph.scenarios.find(s => s.id === data.scenario_guess) : null;
  const notes = asList(data.notes);
  return (
    <div className="rounded-xl border border-secondary-400 bg-white p-3 flex flex-col gap-2 min-w-0 [overflow-wrap:anywhere]">
      <div className="flex items-center gap-x-3 gap-y-1 flex-wrap text-sm">
        <span className="font-bold text-grayscale-1300 font-grotesk">Replay of run #{str(r.id)}</span>
        <StatusPill status={r.status} />
        <span className="text-xs text-grayscale-1200">{MODE_LABEL[str(r.mode)] || str(r.mode)} · trigger {str(r.trigger) || '—'}{r.requested_by ? ` · by ${str(r.requested_by)}` : ''}</span>
        <span className="text-xs text-grayscale-1100">{fmtIST(r.started_at || r.created_at)} · {fmtDur(r.duration_s)}</span>
        <span className="flex-1" />
        <Btn small onClick={() => ctx.openRun(str(r.id))}>Run details</Btn>
        <Btn small onClick={onClear}>Clear replay</Btn>
      </div>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        {runSummary(r) && <span className="rounded-full bg-grayscale-200 px-2.5 py-0.5">{runSummary(r)}</span>}
        <span className="rounded-full bg-grayscale-200 px-2.5 py-0.5">{arr(data.path).length} nodes on the path · {arr(data.steps).length} steps</span>
        {guess && (
          <button type="button" onClick={() => onScenario(guess.id)}
            className="rounded-full border border-secondary-400 text-secondary-500 px-2.5 py-0.5 hover:bg-secondary-100"
            title="Show this scenario's documented path">looks like: {guess.label} →</button>
        )}
      </div>
      {data.error && data.success === false && <Msg kind="error">{str(data.error)}</Msg>}
      {notes.length > 0 && <ul className="list-disc pl-5 text-xs text-grayscale-1200">{notes.map((n, i) => <li key={i}>{n}</li>)}</ul>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// one university's flow
// ---------------------------------------------------------------------------
const FlowForCode: React.FC<{
  code: string; ctx: RecrawlCtx; replayReq: { id: string; n: number } | null; onCode: (c: string) => void;
}> = ({ code, ctx, replayReq, onCode }) => {
  const [tick, setTick] = useState(0);
  // The first load may use the server's 60 s graph cache; Refresh asks for a fresh build.
  const g = useAdminGet<any>(S, 'flow', tick ? { code, refresh: 1 } : { code }, [code, tick]);
  const graph = useMemo(() => sanitizeGraph(g.data), [g.data]);
  const runsRes = useAdminGet<any>(S, 'runs', { code, limit: 40 }, [code, ctx.tick]);
  const runs = useMemo(() => arr(runsRes.data?.runs), [runsRes.data]);

  const [mode, setMode] = useState<FlowMode>('overview');
  const [scenarioId, setScenarioId] = useState<string | null>(null);
  const [replay, setReplay] = useState<{ id: string; data: FlowTrace & Record<string, any> } | null>(null);
  const [rBusy, setRBusy] = useState('');
  const [rErr, setRErr] = useState('');
  const [pickRun, setPickRun] = useState('');
  const [sel, setSel] = useState<Selection | null>(null);
  const [panelView, setPanelView] = useState<'run' | 'generic'>('generic');
  const [orientPref, setOrientPref] = useState<'auto' | 'lr' | 'tb'>('auto');
  const [zoom, setZoom] = useState<'fit' | number>('fit');

  const [outerRef, outerW] = useWidth();
  const [graphRef, graphW] = useWidth();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const wide = outerW >= 1400;

  const loadReplay = useCallback(async (id: string) => {
    if (!id) return;
    setRBusy(id); setRErr('');
    const { data, error } = await panelGet(S, 'flow_replay', { run_id: id });
    setRBusy('');
    if (error || !data) { setRErr(error || 'The replay came back empty.'); return; }
    const rc = str(obj(obj(data).run).code).toUpperCase();
    if (rc && rc !== code) { onCode(rc); return; }     // a run of another university: switch, it replays there
    const d = obj(data);
    setReplay({ id, data: { ...d, path: arr(d.path).map(String), edges: arr(d.edges).map(String), steps: arr(d.steps), results: [] } as any });
    setMode('trace');
    setPanelView('run');
  }, [code, onCode]);
  useEffect(() => { if (replayReq?.id) loadReplay(replayReq.id); }, [replayReq?.n]);  // eslint-disable-line react-hooks/exhaustive-deps

  // --- layout -------------------------------------------------------------------
  const parentOf = useMemo(() => {
    const m = new Map<string, string>();
    graph?.nodes.forEach(n => (n.substeps || []).forEach(s => { if (!m.has(s.id)) m.set(s.id, n.id); }));
    return m;
  }, [graph]);
  const layouts = useMemo(() => (graph && graph.nodes.length
    ? { lr: relabel(computeLayout(graph, 'lr'), graph.laneLabels), tb: relabel(computeLayout(graph, 'tb'), graph.laneLabels) }
    : null), [graph]);
  const avail = Math.max(320, (graphW || 900) - 4);
  const orientation: 'lr' | 'tb' = useMemo(() => {
    if (orientPref !== 'auto' || !layouts) return orientPref === 'tb' ? 'tb' : 'lr';
    const fitLR = avail / layouts.lr.width; const fitTB = avail / layouts.tb.width;
    return fitLR >= 0.85 || fitLR >= fitTB ? 'lr' : 'tb';
  }, [orientPref, layouts, avail]);
  const layout: Layout | null = layouts ? layouts[orientation] : null;
  const scale = layout ? (zoom === 'fit' ? Math.max(0.55, Math.min(1.2, avail / layout.width)) : zoom) : 1;

  // --- path + timeline --------------------------------------------------------------
  const scenario = useMemo(() => graph?.scenarios.find(s => s.id === scenarioId) || null, [graph, scenarioId]);
  const steps: TraceStep[] = useMemo(() => (replay ? replay.data.steps.filter(s => s && s.node_id)
    .map(s => ({ ...s, node_id: String(s.node_id), substep_id: s.substep_id ? String(s.substep_id) : null })) : []), [replay]);
  const { path, pathEdges, timeline } = useMemo(() => {
    const none = { path: [] as string[], pathEdges: new Set<string>(), timeline: null as Timeline | null };
    if (!graph) return none;
    const known = new Set(graph.edges.map(e => e.id));
    if (mode === 'scenario' && scenario) {
      const p = normalizePath(scenario.path, graph, parentOf);
      const e = resolvePathEdges(p, scenario.edges, graph, parentOf);
      return { path: p, pathEdges: new Set<string>([...e.filter(Boolean) as string[], ...(scenario.edges || []).filter(x => known.has(x))]),
               timeline: scenarioTimeline(p, e) };
    }
    if (mode === 'trace' && replay) {
      const p = normalizePath(replay.data.path, graph, parentOf);
      const e = resolvePathEdges(p, replay.data.edges, graph, parentOf);
      return { path: p, pathEdges: new Set<string>([...e.filter(Boolean) as string[], ...(replay.data.edges || []).filter(x => known.has(x))]),
               timeline: traceTimeline(p, e, { ...replay.data, steps }) };
    }
    return none;
  }, [graph, mode, scenario, replay, steps, parentOf]);
  const pathNodes = useMemo(() => new Set(path), [path]);
  const player = usePlayer(timeline);
  const frame = useMemo(() => (layout ? frameAt(timeline, player.t, layout) : null), [timeline, player.t, layout]);

  const nodeStatus = useMemo(() => {
    const m = new Map<string, { status: string; ms: number | null; t: number | null }>();
    if (mode !== 'trace' || !timeline) return m;
    timeline.segs.forEach(s => {
      if (s.kind === 'node' && s.step) m.set(s.nodeId, { status: String(s.step.status), ms: num(s.step.ms), t: num(s.step.t_start_ms) });
    });
    return m;
  }, [mode, timeline]);
  const subSteps = useMemo(() => new Map<string, TraceStep>(), []);
  const nodeSteps = useMemo(() => {
    const m = new Map<string, TraceStep[]>();
    if (mode !== 'trace') return m;
    steps.forEach(s => { m.set(s.node_id, [...(m.get(s.node_id) || []), s]); });
    return m;
  }, [mode, steps]);

  // --- Prev / Next through the path ------------------------------------------------
  const nodeById = useMemo(() => new Map((graph?.nodes || []).map(n => [n.id, n])), [graph]);
  const allOrder = useMemo(() => {
    if (!graph) return [] as string[];
    const lanes = laneOrder(graph.nodes);
    return [...graph.nodes].sort((a, b) => lanes.indexOf(a.lane) - lanes.indexOf(b.lane) || (num(a.order) ?? 0) - (num(b.order) ?? 0))
      .map(n => n.id);
  }, [graph]);
  const expand = useCallback((ids: string[]): NavItem[] => {
    const out: NavItem[] = [];
    const seen = new Set<string>();
    ids.forEach(id => {
      const n = nodeById.get(id);
      if (!n || seen.has(id)) return;
      seen.add(id);
      out.push({ id, label: n.label });
      (n.substeps || []).forEach(s => out.push({ id, sub: s.id, label: `${n.label} › ${s.label}` }));
    });
    return out;
  }, [nodeById]);
  const nav = useMemo(() => {
    const blank = { prev: null, next: null, pos: 0, total: 0, label: '' };
    if (!sel || sel.kind !== 'node') return blank;
    const find = (list: NavItem[]) => list.findIndex(x => x.id === sel.id && (x.sub || '') === (sel.sub || ''));
    let list = path.length ? expand(path) : [];
    let where = mode === 'trace' ? 'this run' : 'this scenario';
    let i = find(list);
    if (i < 0) { list = expand(allOrder); i = find(list); where = 'all steps'; }
    if (i < 0) return blank;
    return { prev: list[i - 1] || null, next: list[i + 1] || null, pos: i + 1, total: list.length, label: where };
  }, [sel, path, expand, allOrder, mode]);

  const select = useCallback((s: Selection) => {
    setSel(s);
    if (s.kind === 'node') setPanelView(mode === 'trace' ? 'run' : 'generic');
  }, [mode]);
  const closePanel = useCallback(() => setSel(null), []);

  useEffect(() => {
    if (!sel || wide) return;
    const el = panelRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.top > window.innerHeight - 80 || r.bottom < 0) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [sel, wide]);
  useEffect(() => {
    if (!sel) return undefined;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.metaKey || e.ctrlKey || e.altKey) return;
      if (document.querySelector('[role="dialog"]')) return;
      if (e.key === 'Escape') setSel(null);
      if (e.key === 'ArrowRight' && nav.next) { e.preventDefault(); setSel({ kind: 'node', id: nav.next.id, sub: nav.next.sub }); }
      if (e.key === 'ArrowLeft' && nav.prev) { e.preventDefault(); setSel({ kind: 'node', id: nav.prev.id, sub: nav.prev.sub }); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sel, nav]);

  const pickScenario = (id: string) => {
    if (mode === 'scenario' && scenarioId === id) { player.restart(); return; }
    setScenarioId(id);
    setMode('scenario');
  };

  // --- render ----------------------------------------------------------------------
  if (g.loading && !graph) return <Loading label={`Building the ${code} pipeline…`} />;
  if (g.error && !graph) {
    return (
      <Section title={`${code} pipeline`} right={<Btn small onClick={() => setTick(t => t + 1)}>Retry</Btn>}>
        <Msg kind="error">Could not load the flow: {g.error}</Msg>
      </Section>
    );
  }
  if (!graph || !layout || !frame) {
    return (
      <Section title={`${code} pipeline`} right={<Btn small onClick={() => setTick(t => t + 1)}>Reload</Btn>}>
        <div className="text-sm text-grayscale-1100 py-6 text-center">The server returned an empty flow graph.</div>
      </Section>
    );
  }

  const finished = !!timeline && player.t >= timeline.total;
  const activeLabel = frame.activeSeg
    ? (frame.activeSeg.kind === 'node' ? nodeById.get(frame.activeSeg.nodeId)?.label : `→ ${nodeById.get(frame.activeSeg.nodeId)?.label || ''}`)
    : finished ? 'done' : '';
  const runOpts = ['', ...runs.map(r => str(r.id))];
  const runLabels: Record<string, string> = { '': runs.length ? 'Pick a run to replay…' : 'No runs yet' };
  runs.forEach(r => {
    runLabels[str(r.id)] = `#${str(r.id)} · ${str(r.status).replace(/_/g, ' ')} · ${MODE_LABEL[str(r.mode)] || str(r.mode)} · ${fmtAgo(r.created_at)}`;
  });

  return (
    <div ref={outerRef} className="flex flex-col gap-4 min-w-0">
      <style>{FLOW_CSS}</style>
      <LiveStrip live={graph.live} generatedAt={graph.generated_at} loading={g.loading} onRefresh={() => setTick(t => t + 1)} />

      <Section title="Scenarios — pick one to watch the path it takes"
        right={mode !== 'overview' ? <Btn small onClick={() => setMode('overview')}>Show whole graph</Btn> : undefined}>
        <div className="flex gap-2 flex-wrap">
          {graph.scenarios.map(s => {
            const on = mode === 'scenario' && scenarioId === s.id;
            const rr = s.real_run ? obj(s.real_run) : null;
            return (
              <button key={s.id} type="button" onClick={() => pickScenario(s.id)} title={s.description || s.label}
                className={`text-left rounded-xl border px-3 py-1.5 max-w-[15rem] transition-colors ${on
                  ? 'bg-secondary-500 border-secondary-500 text-white' : 'border-grayscale-600 bg-white hover:bg-grayscale-200 text-grayscale-1300'}`}>
                <div className="text-xs font-bold font-grotesk truncate">{s.label}</div>
                <div className={`text-[11px] truncate ${on ? 'text-white/80' : 'text-grayscale-1100'}`}>
                  {rr ? `last: #${str(rr.run_id)} ${fmtAgo(rr.date)}` : 'not taken yet'}
                </div>
              </button>
            );
          })}
          {!graph.scenarios.length && <span className="text-sm text-grayscale-1100">No scenarios defined.</span>}
        </div>
        {mode === 'scenario' && scenario && (
          <ScenarioCard s={scenario} graph={graph} onReplay={player.restart} onReplayRun={loadReplay} busy={rBusy} />
        )}
        {mode === 'overview' && (
          <div className="text-xs text-grayscale-1100">
            Pick a scenario to animate its path, click any step for its live parameters (declared vs effective throttle, thresholds,
            budgets), or replay a stored run below and watch it travel through the pipeline at its real pace.
          </div>
        )}
      </Section>

      <Section title="Replay a stored run" right={replay ? <Btn small onClick={() => { setReplay(null); if (mode === 'trace') setMode('overview'); }}>Clear replay</Btn> : undefined}>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={pickRun} onChange={setPickRun} options={runOpts} labels={runLabels} />
          <Btn small kind="primary" disabled={!pickRun || !!rBusy} onClick={() => loadReplay(pickRun)}>
            {rBusy ? `Loading #${rBusy}…` : '▶ Replay'}
          </Btn>
          {replay && mode !== 'trace' && <Btn small onClick={() => setMode('trace')}>Back to run #{replay.id}</Btn>}
          <span className="text-xs text-grayscale-1100">Each step dwells in proportion to the time it really took; the whole replay lasts 4–10 s.</span>
        </div>
        {rErr && <Msg kind="error">{rErr}</Msg>}
      </Section>

      {mode === 'trace' && replay && (
        <ReplayHeader data={replay.data} graph={graph} ctx={ctx} onScenario={id => { setScenarioId(id); setMode('scenario'); }}
          onClear={() => { setReplay(null); setMode('overview'); }} />
      )}

      <div className={wide && sel ? 'grid gap-4 items-start' : 'flex flex-col gap-4'}
        style={wide && sel ? { gridTemplateColumns: 'minmax(0, 1fr) 420px' } : undefined}>
        <div className="bg-white rounded-xl border border-grayscale-300 p-3 flex flex-col gap-2 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {timeline ? (
              <PlayerBar player={player} finished={finished} activeLabel={activeLabel || ''} trace={mode === 'trace'}
                realNow={frame.realNow} realTotal={timeline.realTotal} />
            ) : (
              <span className="text-xs text-grayscale-1100 flex-1">Whole pipeline — {graph.nodes.length} steps, {graph.edges.length} connections.</span>
            )}
            <div className="flex items-center gap-1 ml-auto">
              <Seg value={orientPref} onChange={v => setOrientPref(v as 'auto' | 'lr' | 'tb')}
                options={[['auto', 'Auto'], ['lr', '⇢ Across'], ['tb', '⇣ Down']]} title="Layout direction" />
              <Seg value={zoom === 'fit' ? 'fit' : ''} onChange={() => setZoom('fit')} options={[['fit', 'Fit']]} title="Fit to width" />
              <button type="button" className="w-7 h-7 rounded-full border border-grayscale-600 text-sm hover:bg-grayscale-200"
                title="Zoom out" onClick={() => setZoom(Math.max(0.4, Math.round((scale - 0.1) * 10) / 10))}>−</button>
              <button type="button" className="w-7 h-7 rounded-full border border-grayscale-600 text-sm hover:bg-grayscale-200"
                title="Zoom in" onClick={() => setZoom(Math.min(1.8, Math.round((scale + 0.1) * 10) / 10))}>+</button>
            </div>
          </div>
          <div ref={graphRef} className="overflow-auto rounded-lg border border-grayscale-200 relative" onClick={() => setSel(null)}>
            <div style={{ width: layout.width * scale, margin: layout.width * scale < avail ? '0 auto' : undefined }}>
              <FlowGraph graph={graph} layout={layout} mode={mode} pathNodes={pathNodes} pathEdges={pathEdges}
                frame={frame} nodeStatus={nodeStatus} subSteps={subSteps} selected={sel} onSelect={select} scale={scale} />
            </div>
          </div>
          <FlowLegend showStatus={mode === 'trace'} />
        </div>
        {sel && (
          <div ref={panelRef}
            className={`bg-white rounded-xl border border-grayscale-300 p-4 min-w-0 ${wide ? 'sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto' : ''}`}>
            <FlowNodePanel graph={graph} sel={sel} onSelect={select} onClose={closePanel}
              nodeSteps={nodeSteps} subSteps={subSteps} hasTrace={mode === 'trace' && !!replay}
              view={panelView} setView={setPanelView} nav={nav} pathNodes={pathNodes} />
          </div>
        )}
      </div>

      {mode === 'trace' && replay && (
        <StepTimeline steps={steps} path={path} graph={graph} realTotal={timeline?.realTotal || 0}
          realNow={finished ? null : frame.realNow} onSelect={select} />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// the tab
// ---------------------------------------------------------------------------
const RecrawlFlow: React.FC<{ ctx: RecrawlCtx; focus: ({ code: string; run?: string; n: number }) | null }> = ({ ctx, focus }) => {
  const [code, setCode] = useState(str(focus?.code).toUpperCase());
  const [replayReq, setReplayReq] = useState<{ id: string; n: number } | null>(null);
  useEffect(() => {
    if (!focus) return;
    if (focus.code) setCode(str(focus.code).toUpperCase());
    setReplayReq(r => (focus.run ? { id: focus.run, n: (r?.n || 0) + 1 } : null));
  }, [focus?.n]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (code || !ctx.universities.length) return;
    const withRun = ctx.universities.find(u => u.last_run);
    setCode((withRun || ctx.universities[0]).code);
  }, [ctx.universities, code]);

  const u = ctx.uniByCode.get(code);
  const opts = ctx.universities.map(x => x.code);
  if (code && !opts.includes(code)) opts.unshift(code);
  const labels: Record<string, string> = {};
  ctx.universities.forEach(x => { labels[x.code] = `${x.code} — ${x.name}`; });

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-bold text-grayscale-1300 font-grotesk">Pipeline of</span>
        {opts.length ? (
          <Select value={code} onChange={v => { setReplayReq(null); setCode(v); }} options={opts} labels={labels} />
        ) : <span className="text-sm text-grayscale-1100">{code || 'loading universities…'}</span>}
        {u && <span className="text-xs text-grayscale-1100">{u.kind === 'manual_file' ? 'manual source file' : 'scraper'} · {str(u.platform) || '—'} · {str(u.country) || '—'}</span>}
        <span className="flex-1" />
        {code && <Btn small onClick={() => ctx.openUniversity(code)}>University details</Btn>}
      </div>
      {code ? <FlowForCode key={code} code={code} ctx={ctx} replayReq={replayReq} onCode={c => setCode(c)} /> : <Loading label="Loading universities…" />}
    </>
  );
};

export default RecrawlFlow;
